import { useState } from "react";
import {
  Alert, Box, Button, Chip, CircularProgress, MenuItem, Paper, Stack, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import { useQuery } from "@tanstack/react-query";
import { scheduleRegisterApi } from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import { PageHeader } from "@/components/PageHeader";
import { StatCard } from "@/components/StatCard";
import { formatDate } from "@/shared/datetime";

/** First and last day of the current month — the period an inspection usually asks for. */
function monthBounds(d = new Date()) {
  const iso = (x: Date) =>
    `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`;
  return {
    from: iso(new Date(d.getFullYear(), d.getMonth(), 1)),
    to: iso(new Date(d.getFullYear(), d.getMonth() + 1, 0)),
  };
}

/**
 * The Schedule H1 supply register (W21).
 *
 * <p>Statutory. The Drugs and Cosmetics Rules require a separate register of H1 supplies —
 * prescriber, patient, drug, quantity, date — retained for three years and produced on inspection.
 * The item master has flagged these drugs since it was built and nothing was captured at the
 * counter, so this could not be produced at all.</p>
 *
 * <p><b>Not the narcotic register.</b> That one, under Inventory, projects stock movements from the
 * ledger with a running balance — the form for stock accountability. It cannot answer this one: the
 * ledger knows batches and quantities and has never known a patient or a prescriber.</p>
 *
 * <p>Laid out to be printed. An inspector reads a register on paper, in date order, so the screen
 * is a table in that order and the print stylesheet drops everything around it.</p>
 */
export function ScheduleRegisterPage() {
  const bounds = monthBounds();
  const [from, setFrom] = useState(bounds.from);
  const [to, setTo] = useState(bounds.to);
  const [category, setCategory] = useState("");

  const query = useQuery({
    queryKey: ["schedule-register", from, to, category],
    queryFn: () => scheduleRegisterApi.list({
      from: from || undefined,
      to: to ? `${to}T23:59:59` : undefined,
      scheduleCategory: category || undefined,
    }),
  });

  const rows = query.data ?? [];
  // Entries missing what the register is required to carry. These are bills raised before the
  // capture existed — worth surfacing, because an inspection will find them before anybody else.
  const incomplete = rows.filter(r => !r.prescriberName || !r.prescriberRegNo).length;

  return (
    <Box>
      {/* Per-page, as the barcode screen does it — there is no global print stylesheet, and an
          inspector reads this on paper without the filters and the navigation around it. */}
      <style>{`@media print {
        .no-print { display: none !important; }
        .MuiPaper-root { box-shadow: none !important; }
      }`}</style>

      <PageHeader
        breadcrumbs={["Sales"]}
        title="Schedule H1 register"
        subtitle="Every Schedule H1 and X supply, in date order: patient, prescriber, drug and quantity. Retain for three years and produce on inspection."
        actions={
          <Button variant="outlined" startIcon={<PrintIcon />} onClick={() => window.print()}
            className="no-print">
            Print
          </Button>
        }
      />

      <Paper sx={{ p: 2, mb: 2 }} className="no-print">
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <TextField type="date" label="From" size="small" InputLabelProps={{ shrink: true }}
            value={from} onChange={(e) => setFrom(e.target.value)} />
          <TextField type="date" label="To" size="small" InputLabelProps={{ shrink: true }}
            value={to} onChange={(e) => setTo(e.target.value)} helperText="Inclusive" />
          <TextField select label="Schedule" size="small" sx={{ minWidth: 160 }}
            value={category} onChange={(e) => setCategory(e.target.value)}>
            <MenuItem value="">H1 and X</MenuItem>
            <MenuItem value="H1">H1 only</MenuItem>
            <MenuItem value="X">X only</MenuItem>
          </TextField>
        </Stack>
      </Paper>

      <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: "wrap" }} className="no-print">
        <StatCard label="Supplies" value={rows.length}
          hint={`${formatDate(from)} to ${formatDate(to)}`} />
        <StatCard label="Distinct drugs" value={new Set(rows.map(r => r.itemId)).size} />
        <StatCard label="Incomplete entries" value={incomplete}
          severity={incomplete ? "critical" : "default"}
          hint={incomplete ? "missing prescriber details" : "every entry complete"} />
      </Stack>

      {incomplete > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }} className="no-print">
          <b>{incomplete} entr{incomplete === 1 ? "y is" : "ies are"} missing prescriber details.</b>{" "}
          These were dispensed before the capture existed — the bill cannot be raised without them
          now. They cannot be filled in retrospectively from anything the system holds, so the
          prescriptions themselves are the only source.
        </Alert>
      )}

      {query.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(query.error)}</Alert>}
      {query.isLoading && (
        <Stack alignItems="center" sx={{ py: 4 }}><CircularProgress size={24} /></Stack>
      )}

      <Paper sx={{ p: 2 }}>
        <Box sx={{ display: "none", "@media print": { display: "block", mb: 2 } }}>
          <Typography variant="h6">Schedule H1 / X supply register</Typography>
          <Typography variant="caption">
            {formatDate(from)} to {formatDate(to)} · {rows.length} entries
          </Typography>
        </Box>

        {!query.isLoading && rows.length === 0 && (
          <Alert severity="info">
            No Schedule H1 or X supplies in this period. Only posted bills appear — a draft
            dispensed nothing.
          </Alert>
        )}

        {rows.length > 0 && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 100 }}>Date</TableCell>
                  <TableCell sx={{ width: 130 }}>Bill</TableCell>
                  <TableCell>Patient</TableCell>
                  <TableCell>Drug</TableCell>
                  <TableCell sx={{ width: 130 }}>Batch</TableCell>
                  <TableCell align="right" sx={{ width: 70 }}>Qty</TableCell>
                  <TableCell>Prescriber</TableCell>
                  <TableCell sx={{ width: 120 }}>Rx ref</TableCell>
                  <TableCell sx={{ width: 130 }}>Dispensed by</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r, i) => {
                  const missing = !r.prescriberName || !r.prescriberRegNo;
                  return (
                    <TableRow key={`${r.documentNo}-${r.itemId}-${i}`} hover>
                      <TableCell>{formatDate(r.suppliedAt)}</TableCell>
                      <TableCell>{r.documentNo}</TableCell>
                      <TableCell>
                        <Stack spacing={0}>
                          <span>{r.patientName ?? "—"}</span>
                          <Typography variant="caption" color="text.secondary">
                            {[r.uhid, r.patientPhone].filter(Boolean).join(" · ")}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack spacing={0}>
                          <span>{r.drugName}</span>
                          <Typography variant="caption" color="text.secondary">
                            {r.sku}
                            {r.scheduleCategory && (
                              <Chip size="small" variant="outlined" label={r.scheduleCategory}
                                sx={{ ml: 0.5, height: 16, fontSize: 10 }} />
                            )}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        {r.batchNo ?? "—"}
                        {r.expiryDate && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                            exp {formatDate(r.expiryDate)}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">{r.qty}</TableCell>
                      <TableCell>
                        {missing ? (
                          <Typography variant="caption" color="error.main">
                            not recorded
                          </Typography>
                        ) : (
                          <Stack spacing={0}>
                            <span>{r.prescriberName}</span>
                            <Typography variant="caption" color="text.secondary">
                              Reg. {r.prescriberRegNo}
                            </Typography>
                          </Stack>
                        )}
                      </TableCell>
                      <TableCell>{r.prescriptionRef ?? "—"}</TableCell>
                      <TableCell>{r.dispensedBy ?? "—"}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
}
